import { useEffect, useState } from 'react';
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { useFetchWrapper } from '../../hooks/useFetchWrapper';
import { US_STATES } from "../../utilities/constValues";
import Helper from '../../utilities/helper';
import { clientConfig } from '../../utilities/configService';
import { FormContainer } from '../Shared/Forms/FormContainer';
import { FormHeading } from '../Shared/Forms/FormHeading';
import { IUserProfile } from './MyAccount';

interface IJobRoleType {
    jobRoleTypeID: number,
    jobRoleTypeName: string
}

interface IOrganizationType {
    organizationTypeID: string,
    organizationTypeName: string
}

interface IProfileProps {
    userProfile: IUserProfile
    saveHandler: () => void,
}

export const EditMyAccount = ({ userProfile, saveHandler }: IProfileProps) => {
    const fetchWrapper = useFetchWrapper();
    const applicationName = clientConfig?.APPLICATION_NAME || ''; //clientApplicationConfig.APPLICATION_NAME ? clientApplicationConfig.APPLICATION_NAME : '';
    const [jobRoleTypeOptions, setJobRoleTypeOptions] = useState([]);
    const [jobRoleTypeSelect, setJobRoleTypeSelect] = useState(0);
    const [organizationTypeSelect, setOrganizationTypeSelect] = useState("0");
    const [organizationTypeOptions, setOrganizationTypeOptions] = useState([]);
    const [stateProvinceRegionSelect, setStateProvinceRegionSelect] = useState("0");
    const [formErrors, setFormErrors] = useState({
        firstName: '',
        lastName: '',
        organizationName: '',
        organizationType: '',
        phone: '',
        postalCode: '',
    });

    const [profileForm, setProfileForm] = useState<IUserProfile>({
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        userName: "",
        email: userProfile.email,
        title: userProfile.title,
        jobRoleTypeID: userProfile.jobRoleTypeID,
        jobRoleTypeName: userProfile.jobRoleTypeName,
        organizationName: userProfile.organizationName,
        organizationTypeID: userProfile.organizationTypeID,
        organizationTypeName: userProfile.organizationTypeName,
        phone: userProfile.phone,
        addressLine1: userProfile.addressLine1,
        addressLine2: userProfile.addressLine2,
        city: userProfile.city,
        stateProvinceRegion: userProfile.stateProvinceRegion,
        postalCode: userProfile.postalCode,
        isParticipant: userProfile.isParticipant
    });



    useEffect(() => {
        populateOrganizationType();
        populateJobRoleType();
        if (stateProvinceRegionSelect === "0" && userProfile.stateProvinceRegion !== "") {
            setStateProvinceRegionSelect(userProfile.stateProvinceRegion);
        }
    }, []);

    const populateOrganizationType = () => {
        fetchWrapper.get('organization/gettypes')
            .then((data) => {
                setOrganizationTypeOptions(data.organizationTypes);
                setOrganizationTypeSelect(userProfile.organizationTypeID.toString());
            });
    };

    const populateJobRoleType = () => {
        fetchWrapper.get(`jobrole`).then((data) => {
            if (data.jobRoles) {
                // generate the role list for the select field
                setJobRoleTypeOptions(data.jobRoles);
                // check if title value exists
                if (userProfile.jobRoleTypeID) {
                    // set the saved select value
                    setJobRoleTypeSelect(userProfile.jobRoleTypeID);
                }
            }
            else {
                console.log("Job Roles not found.")
            }
        });
    };

    function handleFirstNameChange(e: { target: { value: any; }; }) {
        setFormErrors({
            ...formErrors,
            firstName: ''
        });

        const updatedForm = {
            ...profileForm,
            firstName: e.target.value
        };
        setProfileForm(updatedForm);
        if (formErrors.firstName !== '') {
            validateForm(updatedForm);
        }
    }

    function handleLastNameChange(e: { target: { value: any; }; }) {
        setFormErrors({
            ...formErrors,
            lastName: ''
        });

        const updatedForm = {
            ...profileForm,
            lastName: e.target.value
        };
        setProfileForm(updatedForm);
        if (formErrors.lastName !== '') {
            validateForm(updatedForm);
        }
    }

    //
    const handleJobRoleTypeChange = (e: { target: { value: any; }; }) => {
        // update role with new selection value
        setJobRoleTypeSelect(e.target.value);
    };

    function handlePhoneChange(e: { target: { value: any; }; }) {
        setFormErrors({
            ...formErrors,
            phone: ''
        });

        var formattedPhone = Helper.formatAsPhone(e.target.value);

        setProfileForm({
            ...profileForm,
            phone: formattedPhone ? formattedPhone : e.target.value
        });
    }

    function handleOrganizationNameChange(e: { target: { value: any; }; }) {
        setFormErrors({
            ...formErrors,
            organizationName: ''
        });

        const updatedForm = {
            ...profileForm,
            organizationName: e.target.value
        };
        setProfileForm(updatedForm);

        if (formErrors.organizationName !== '') {
            validateForm(updatedForm);
        }
    }

    function handleStreetAddressChange(e: { target: { value: any; }; }) {
        setProfileForm({
            ...profileForm,
            addressLine1: e.target.value
        });
    }

    function handleStreetAddress2Change(e: { target: { value: any; }; }) {
        setProfileForm({
            ...profileForm,
            addressLine2: e.target.value
        });
    }

    function handleCityChange(e: { target: { value: any; }; }) {
        setProfileForm({
            ...profileForm,
            city: e.target.value
        });
    }

    function handleStateProvinceRegionChange(e: { target: { value: any; }; }) {
        setProfileForm({
            ...profileForm,
            stateProvinceRegion: e.target.value
        });
        setStateProvinceRegionSelect(e.target.value);
    }

    function handlePostalCodeChange(e: { target: { value: any; }; }) {
        setFormErrors({
            ...formErrors,
            postalCode: ''
        });

        setProfileForm({
            ...profileForm,
            postalCode: e.target.value
        });
    }

    const handleOrganizationTypeChange = (e: { target: { value: any; }; }) => {
        setOrganizationTypeSelect(e.target.value);
        if (formErrors.organizationType !== '') {
            validateForm(profileForm, e.target.value);
        }
    };

    const containsOnlySpacesValidationPattern = "^\\s+$";

    //Validate Form
    const errorClass = (error: string | any[]) => {
        return (error.length === 0 ? '' : 'is-invalid');
    }

    // Change handlers pass in the values they just set, since state updates are not visible until the next render.
    const validateForm = (form = profileForm, organizationType = organizationTypeSelect) => {
        var fieldValidationError = '';
        var fieldErrors = {
            firstName: '',
            lastName: '',
            organizationName: '',
            organizationType: '',
            phone: '',
            postalCode: ''
        }

        if (form.firstName == null ||
            form.firstName.length <= 0 ||
            form.firstName.match(containsOnlySpacesValidationPattern)) {
            fieldValidationError = 'First Name is required.';
            fieldErrors.firstName = 'First Name is required.';
        }

        if (form.lastName == null ||
            form.lastName.length <= 0 ||
            form.lastName.match(containsOnlySpacesValidationPattern)) {
            fieldValidationError = 'Last Name is required.';
            fieldErrors.lastName = 'Last Name is required.';
        }

        if (form.organizationName == null ||
            form.organizationName.length <= 0 ||
            form.organizationName.match(containsOnlySpacesValidationPattern)) {
            fieldValidationError = 'Organization Name is required.';
            fieldErrors.organizationName = 'Organization Name is required.';
        }

        if (organizationType === '0') {
            fieldValidationError = 'Organization Type is required.';
            fieldErrors.organizationType = 'Organization Type is required.'
        }

        if (form.phone && form.phone.length > 0) {
            if (form.phone.length < 10 || form.phone.length > 15) {
                fieldValidationError = 'Phone number is invalid.';
                fieldErrors.phone = 'Phone number is invalid.';
            }
        }

        if (form.postalCode && form.postalCode.length > 0) {
            if (form.postalCode.length > 10 || form.postalCode.length < 5) {
                fieldValidationError = 'Postal code length is invalid.';
                fieldErrors.postalCode = 'Postal code length is invalid.';
            }
        }

        // Always show the latest result, so fixing the last remaining error clears it too.
        setFormErrors(fieldErrors);

        if (fieldValidationError.length > 0) {
            console.log("formErrors state: ", formErrors);
            return false;
        }
        else {
            return true;
        }
    }

    const validateAndPostForm = async (e: any) => {

        e.preventDefault();

        let formIsValid = validateForm();

        if (formIsValid) {
            var accountUpdateRequest = {
                firstName: profileForm.firstName,
                lastName: profileForm.lastName,
                jobRoleTypeID: jobRoleTypeSelect !== 0 ? jobRoleTypeSelect : null,
                email: profileForm.email,
                phone: profileForm.phone,
                title: '',
                organizationName: profileForm.organizationName,
                organizationTypeID: organizationTypeSelect !== '0' ? organizationTypeSelect : null,
                addressLine1: profileForm.addressLine1,
                addressLine2: profileForm.addressLine2 ? profileForm.addressLine2 : '',
                city: profileForm.city,
                stateProvinceRegion: profileForm.stateProvinceRegion,
                county: '',
                postalCode: profileForm.postalCode,
                applicationName: applicationName
            };

            fetchWrapper.post('Account/Update', 'application/json', accountUpdateRequest)
                .then((data) => {
                    if (data.success) {
                        saveHandler();
                    }
                    else {
                        //should log this somewhere?
                        saveHandler();
                    }
                });
        }
    };

    const onCancel = () => {
        saveHandler();
    }

    return (
        <Col className="profileRequestFormContainer">
            <FormContainer title="" description="">
                <Row className="profileRequestForm">
                    <Col className="formBody">
                        <FormHeading title="My Profile" textAlign="alignCenter" light={false} />
                        <div className="formInnerBody">
                            <Row className="justify-content-end">
                                <Col sm={8} className="formRequiredContainer">
                                    <span className="formRequired"></span> = required field
                                </Col>
                            </Row>
                            <Form>
                                <FormGroup row className={`form-group ${errorClass(formErrors.firstName)}`}>
                                    <Label for="firstName" sm={5} className="formRequired ">First Name</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="text" name="firstName" id="firstName" placeholder="" required maxLength={150} value={profileForm.firstName} onChange={handleFirstNameChange} invalid={formErrors.firstName !== ''} pattern={containsOnlySpacesValidationPattern.toString()} />
                                        <FormFeedback>{formErrors.firstName}</FormFeedback>
                                    </Col>
                                </FormGroup>
                                <FormGroup row className={`form-group ${errorClass(formErrors.lastName)}`}>
                                    <Label for="lastName" sm={5} className="formRequired">Last Name</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="text" name="lastName" id="lastName" placeholder="" required maxLength={150} value={profileForm.lastName} onChange={handleLastNameChange} invalid={formErrors.lastName !== ''} pattern={containsOnlySpacesValidationPattern.toString()} />
                                        <FormFeedback>{formErrors.lastName}</FormFeedback>
                                    </Col>
                                </FormGroup>
                                <FormGroup row className="form-group">
                                    <Label for="jobRoleType" sm={5} className="notRequired">Job Role</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="select" name="jobRoleType" id="jobRoleType" value={jobRoleTypeSelect} onChange={handleJobRoleTypeChange}>
                                            <option key="0" value="0" disabled>
                                                Select an option
                                            </option>
                                            {jobRoleTypeOptions.map((option: IJobRoleType) => {
                                                return (
                                                    <option key={option.jobRoleTypeID} value={option.jobRoleTypeID}>
                                                        {option.jobRoleTypeName}
                                                    </option>)
                                            })};
                                        </Input>
                                    </Col>
                                </FormGroup>
                                <FormGroup row className={`form-group ${errorClass(formErrors.phone)}`}>
                                    <Label for="phone" sm={5} className="notRequired">Phone number</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="text" name="phone" id="phone" placeholder="" maxLength={150} value={profileForm.phone} onChange={handlePhoneChange} invalid={formErrors.phone !== ''} />
                                        <FormFeedback>{formErrors.phone}</FormFeedback>
                                    </Col>
                                </FormGroup>
                                <hr />
                                <FormGroup row className={`form-group ${errorClass(formErrors.organizationName)}`}>
                                    <Label for="organizationName" sm={5} className="formRequired">Organization Name</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="text" name="organizationName" id="organizationName" placeholder="" maxLength={150} value={profileForm.organizationName} onChange={handleOrganizationNameChange} invalid={formErrors.organizationName !== ''} />
                                        <FormFeedback>{formErrors.organizationName}</FormFeedback>
                                    </Col>
                                </FormGroup>
                                <FormGroup row className={`form-group ${errorClass(formErrors.organizationType)}`}>
                                    <Label for="organizationType" sm={5} className="formRequired">Organization Type</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="select" name="select" id="organizationType" value={organizationTypeSelect} required onChange={handleOrganizationTypeChange} invalid={formErrors.organizationType !== ''}>
                                            <option key="0" value="0" disabled>
                                                Select an option
                                            </option>
                                            {organizationTypeOptions.map((option: IOrganizationType) => {
                                                return (
                                                    <option key={option.organizationTypeID} value={option.organizationTypeID}>
                                                        {option.organizationTypeName}
                                                    </option>)
                                            })};
                                        </Input>
                                        <FormFeedback>{formErrors.organizationType}</FormFeedback>
                                    </Col>
                                </FormGroup>
                                <FormGroup row className="form-group">
                                    <Label for="street" sm={5} className="notRequired">Organization Street Address Line 1</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="text" name="street" id="street" placeholder="" maxLength={150} value={profileForm.addressLine1} onChange={handleStreetAddressChange} />
                                    </Col>
                                </FormGroup>
                                <FormGroup row className="form-group">
                                    <Label for="street2" sm={5} className="notRequired">Organization Street Address Line 2</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="text" name="street2" id="street2" placeholder="" maxLength={150} value={profileForm.addressLine2} onChange={handleStreetAddress2Change} />
                                    </Col>
                                </FormGroup>
                                <FormGroup row className="form-group">
                                    <Label for="city" sm={5} className="notRequired">Organization Town/City</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth215" type="text" name="city" id="city" placeholder="" maxLength={150} value={profileForm.city} onChange={handleCityChange} />
                                    </Col>
                                </FormGroup>
                                <FormGroup row className="form-group">
                                    <Label for="stateProvinceRegion" sm={5} className="notRequired">Organization State:</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth75" type="select" name="select" id="stateProvinceRegion" value={stateProvinceRegionSelect} onChange={handleStateProvinceRegionChange}>
                                            <option key="0" value="0" disabled>
                                                &nbsp;
                                            </option>
                                            {US_STATES.map((option: string) => {
                                                return (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>)
                                            })};
                                        </Input>
                                    </Col>
                                </FormGroup>
                                <FormGroup row className={`form-group ${errorClass(formErrors.postalCode)}`}>
                                    <Label for="postalCode" sm={5} className="notRequired">Organization ZIP Code</Label>
                                    <Col sm={7}>
                                        <Input className="fieldWidth100" type="text" name="postalCode" id="postalCode" placeholder="" maxLength={150} value={profileForm.postalCode} onChange={handlePostalCodeChange} invalid={formErrors.postalCode !== ''} />
                                        <FormFeedback>{formErrors.postalCode}</FormFeedback>
                                    </Col>
                                </FormGroup>
                                <hr />
                                <div className="buttonRow">
                                    <Button className="cancelButton hollowPos secondaryButton iconReturnArrowBlack" onClick={onCancel}>CANCEL</Button>
                                    <Button className="submitButton solidNeg secondaryButton iconRightArrow" onClick={validateAndPostForm}>Submit</Button>
                                </div>
                            </Form>
                        </div>
                    </Col>
                </Row>
            </FormContainer>
        </Col>
    )
};
